import { useEffect, useState } from "react";
import api, { apiErr, setToken } from "@/lib/api";
import { toast } from "sonner";
import { Eye, ShieldCheck } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { WaTemplates, AgencyWaOverrides } from "@/components/WaTemplatesAdmin";

const card = "bg-white rounded-[16px] p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-slate-100";
const btn = "bg-ec-green text-white px-3 py-1.5 rounded-lg text-sm font-semibold hover:bg-ec-greenDark transition-colors disabled:opacity-50";
const num = (v) => (v === "" || v === null || v === undefined ? null : Number(v));

// Pede confirmação quando o servidor avisa que a exceção é menos exigente (409).
export async function putConfirm(url, body) {
  try { return (await api.put(url, body)).data; }
  catch (e) {
    if (e.response?.status !== 409 || !window.confirm(`${e.response.data.detail}\n\nGuardar mesmo assim?`)) throw e;
    return (await api.put(url, { ...body, confirm: true })).data;
  }
}

function DocRow({ d, onSave, testid }) {
  const [v, setV] = useState(d.validity_months ?? "");
  const [o, setO] = useState(d.offset_days ?? 0);
  const dirty = String(v) !== String(d.validity_months ?? "") || String(o) !== String(d.offset_days ?? 0);
  return (
    <tr className="border-t border-slate-100" data-testid={testid}>
      <td className="py-2 pr-3 text-slate-800">{d.name}</td>
      <td className="py-2 pr-3"><Input type="number" value={v} placeholder="—" onChange={(e) => setV(e.target.value)} className="w-24 h-8" data-testid={`${testid}-validity`} /></td>
      <td className="py-2 pr-3"><Input type="number" value={o} disabled={d.base === "cpcv"} onChange={(e) => setO(e.target.value)} className="w-24 h-8" data-testid={`${testid}-offset`} /></td>
      <td className="py-2 text-right"><button disabled={!dirty} onClick={() => onSave(d.key, num(v), num(o) || 0)} className={btn} data-testid={`${testid}-save`}>Guardar</button></td>
    </tr>
  );
}

function DocDefaults({ data, setData }) {
  const save = async (key, validity_months, offset_days) => {
    try { setData(await putConfirm("/platform/rules/doc-defaults", { key, validity_months, offset_days })); toast.success("Valor por defeito guardado"); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  return (
    <div className={card} data-testid="admin-doc-defaults">
      <h3 className="font-heading text-lg text-ec-green">Validade e antecedência dos documentos</h3>
      <p className="text-sm text-slate-500 mt-1 max-w-2xl">Valores por defeito para todas as agências. A Direção só pode pôr uma validade mais curta ou uma antecedência maior (mais dias antes da escritura, por exemplo −30 em vez de −10). Validade vazia: sem validade.</p>
      <div className="mt-3 overflow-x-auto"><table className="w-full text-sm">
        <thead><tr className="text-left text-xs text-slate-500"><th className="py-1.5 font-medium">Documento</th><th className="py-1.5 font-medium">Validade (meses)</th><th className="py-1.5 font-medium">Antecedência (dias)</th><th /></tr></thead>
        <tbody>{data.docs.map((d) => <DocRow key={`${d.key}-${d.validity_months}-${d.offset_days}`} d={d} onSave={save} testid={`admin-doc-${d.key}`} />)}</tbody>
      </table></div>
    </div>
  );
}

function AutoRow({ r, q, onToggle }) {
  const note = r.one_click ? "Documento com valores (preços, sinais, IBAN): nunca tem aceitação automática" : r.id_card ? "Só parcial: cada cartão novo é sempre confirmado uma vez por uma pessoa" : null;
  return (
    <div className="grid grid-cols-[1fr_auto] md:grid-cols-[1fr_auto_auto] gap-3 items-center py-2.5" data-testid={`admin-auto-${r.key}`}>
      <div className="min-w-0"><div className="text-[15px] text-slate-800">{r.name}</div>{note && <div className="text-xs text-slate-500">{note}</div>}</div>
      <div className="text-xs text-slate-500 tabular-nums md:text-right col-span-2 md:col-span-1 order-last md:order-none" data-testid={`admin-auto-quality-${r.key}`}>
        {q ? `${q.reads} lido${q.reads === 1 ? "" : "s"} · ${q.ok_pct === null ? "—" : `${q.ok_pct}%`} sem correção · ${q.false} falso${q.false === 1 ? "" : "s"} alarme${q.false === 1 ? "" : "s"}` : "0 lidos"}
      </div>
      <Switch checked={r.allowed} disabled={r.one_click} onCheckedChange={(v) => onToggle(r, v)} data-testid={`admin-auto-switch-${r.key}`} />
    </div>
  );
}

function AutoAcceptRules({ data, setData }) {
  const [q, setQ] = useState(null);
  const [pct, setPct] = useState(data.sample_pct);
  useEffect(() => { api.get("/platform/rules/auto-quality").then(({ data: d }) => setQ(d)).catch(() => setQ({})); }, []);
  const toggle = async (r, allowed) => {
    try { setData((await api.put("/platform/rules/auto-accept", { key: r.key, allowed })).data); toast.success(allowed ? `Permitida: ${r.name}` : `Não permitida: ${r.name}`); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const savePct = async () => {
    try { setData((await api.put("/platform/rules/sample", { pct: num(pct) })).data); toast.success("Amostra guardada"); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  return (
    <div className={card} data-testid="admin-auto-accept">
      <h3 className="font-heading text-lg text-ec-green">Aceitação automática</h3>
      <p className="text-sm text-slate-500 mt-1 max-w-2xl">Tipos de documento em que as agências podem ligar a aceitação automática. A Direção só pode desligar, nunca ligar um tipo que não esteja permitido aqui.</p>
      <div className="mt-4 flex flex-wrap items-center gap-3 text-sm text-slate-700">
        Amostra por defeito
        <Input type="number" value={pct} onChange={(e) => setPct(e.target.value)} className="w-20 h-9" data-testid="admin-sample-default" /> %
        <button onClick={savePct} disabled={String(pct) === String(data.sample_pct)} className={btn} data-testid="admin-sample-default-save">Guardar</button>
        <span className="text-xs text-slate-500">Agências novas e pilotos: {data.new_sample_pct}% nos primeiros {data.new_days} dias.</span>
      </div>
      <div className="mt-3 divide-y divide-slate-100">{data.auto.map((r) => <AutoRow key={r.key} r={r} q={q?.[r.key]} onToggle={toggle} />)}</div>
    </div>
  );
}

function Webhooks({ data }) {
  return (
    <div className={card} data-testid="admin-webhooks">
      <h3 className="font-heading text-lg text-ec-green">Endereços dos webhooks</h3>
      <p className="text-sm text-slate-600 mt-2 break-all" data-testid="admin-webhook-twilio">Twilio (sandbox, «When a message comes in»): <span className="font-mono text-xs">{data.webhooks.twilio}</span></p>
      <p className="text-sm text-slate-600 mt-1 break-all" data-testid="admin-webhook-assinaja">AssinaJá (vai em cada documento enviado): <span className="font-mono text-xs">{data.webhooks.assinaja}</span></p>
    </div>
  );
}

function AuditLog({ tick }) {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/platform/audit").then(({ data }) => setRows(data)).catch(() => {}); }, [tick]);
  const show = (v) => (v === null || v === undefined ? "—" : typeof v === "object" ? Object.entries(v).map(([k, x]) => `${k}: ${Array.isArray(x) ? x.join(", ") || "—" : x ?? "—"}`).join(" · ") : String(v));
  return (
    <div className={card} data-testid="admin-audit">
      <h3 className="font-heading text-lg text-ec-green">Registo de alterações</h3>
      {!rows.length ? <p className="text-sm text-slate-500 mt-1">Ainda sem alterações.</p> : (
        <ul className="mt-3 divide-y divide-slate-100 text-sm max-h-96 overflow-y-auto">
          {rows.map((r) => (
            <li key={r.id} className="py-2" data-testid={`admin-audit-${r.id}`}>
              <div className="text-slate-800">{r.what}{r.key ? ` · ${r.key}` : ""}{r.agency_name ? ` · ${r.agency_name}` : " · todas as agências"}</div>
              <div className="text-xs text-slate-500">{new Date(r.at).toLocaleString("pt-PT")} · {r.by} · antes: {show(r.before)} → depois: {show(r.after)}</div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function PlatformRules() {
  const [data, setData] = useState(null);
  const [tick, setTick] = useState(0);
  useEffect(() => { api.get("/platform/rules").then(({ data: d }) => setData(d)).catch(() => {}); }, []);
  if (!data) return null;
  const update = (d) => { setData(d); setTick((t) => t + 1); };
  return (
    <div className="space-y-6" data-testid="admin-platform-rules">
      <div className="flex items-center gap-2 pt-4"><ShieldCheck size={20} className="text-ec-green" /><h2 className="font-heading text-2xl text-ec-green">Regras da plataforma</h2></div>
      <p className="text-sm text-slate-500 -mt-4 max-w-2xl">A Direção de cada agência só pode tornar estas regras mais exigentes, nunca mais permissivas.</p>
      <AgencyRulesList data={data} setData={update} />
      <DocDefaults data={data} setData={update} />
      <AutoAcceptRules data={data} setData={update} />
      <WaTemplates data={data} setData={update} />
      <Webhooks data={data} />
      <AuditLog tick={tick} />
    </div>
  );
}

function AgencyRulesList({ data, setData }) {
  return (
    <div className={card} data-testid="admin-agency-rules">
      <h3 className="font-heading text-lg text-ec-green">Por agência</h3>
      <ul className="mt-2">{data.agencies.map((a) => <AgencyRules key={a.id} a={a} data={data} setData={setData} />)}</ul>
    </div>
  );
}

function AgencyRules({ a, data, setData }) {
  const [open, setOpen] = useState(false);
  const run = async (fn, ok) => { try { setData(await fn()); toast.success(ok); } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } };
  const put = (path, body) => api.put(`/platform/agencies/${a.id}/${path}`, body).then((r) => r.data);
  const enter = async () => {
    try { const { data: d } = await api.post(`/platform/agencies/${a.id}/view-as`); if (d.access_token) setToken(d.access_token); window.location.assign("/inicio"); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  return (
    <li className="py-3 border-t border-slate-100 first:border-t-0" data-testid={`admin-rules-${a.id}`}>
      <div className="flex flex-wrap items-center gap-3">
        <button onClick={() => setOpen(!open)} className="font-semibold text-ec-green text-left flex-1 min-w-[12rem]" data-testid={`admin-rules-open-${a.id}`}>{a.name} {a.is_demo && <span className="ml-1 text-[11px] font-semibold bg-amber-100 text-amber-800 rounded-full px-2 py-0.5">Demonstração</span>}</button>
        <span className="text-xs text-slate-500">Amostra {a.sample_pct}% · KYC {a.kyc_enabled ? "ligada" : "desligada"}</span>
        {a.is_demo && <button onClick={enter} className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg border border-slate-200 hover:bg-slate-50" data-testid={`admin-view-as-${a.id}`}><Eye size={14} /> Ver como</button>}
      </div>
      {open && (
        <div className="mt-3 space-y-4 pl-1">
          <label className="flex items-center gap-3 text-sm text-slate-700">
            <Switch checked={a.kyc_enabled} disabled={!a.is_demo} onCheckedChange={(v) => run(() => put("kyc", { enabled: v }), "Recolha de identificação guardada")} data-testid={`admin-kyc-${a.id}`} />
            Recolha de identificação (Lei 83/2017){!a.is_demo && <span className="text-xs text-slate-500">sempre ligada; só se desliga em agências de demonstração</span>}
          </label>
          <label className="flex items-center gap-3 text-sm text-slate-700">
            <Switch checked={a.pilot} onCheckedChange={(v) => run(() => put("pilot", { enabled: v }), "Piloto guardado")} data-testid={`admin-pilot-${a.id}`} />
            Piloto {a.new_window && <span className="text-xs text-slate-500">(nos primeiros {data.new_days} dias: amostra mínima {data.new_sample_pct}%)</span>}
          </label>
          <SampleException a={a} data={data} setData={setData} />
          {a.is_demo && <TwilioAdmin a={a} />}
          <AgencyWaOverrides a={a} data={data} setData={setData} />
          <DocExceptions a={a} data={data} setData={setData} />
        </div>
      )}
    </li>
  );
}

function SampleException({ a, data, setData }) {
  const [pct, setPct] = useState(a.sample_override ?? "");
  const save = async (value) => {
    try { setData(await putConfirm(`/platform/agencies/${a.id}/sample`, { pct: value })); toast.success("Amostra da agência guardada"); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  return (
    <div className="flex flex-wrap items-center gap-2 text-sm text-slate-700" data-testid={`admin-sample-${a.id}`}>
      Exceção da amostra <Input type="number" value={pct} placeholder={String(data.sample_pct)} onChange={(e) => setPct(e.target.value)} className="w-20 h-8" data-testid={`admin-sample-input-${a.id}`} /> %
      <button onClick={() => save(num(pct))} disabled={String(pct) === String(a.sample_override ?? "")} className={btn} data-testid={`admin-sample-save-${a.id}`}>Guardar</button>
      {a.sample_override !== null && a.sample_override !== undefined && <button onClick={() => { setPct(""); save(null); }} className="text-xs text-slate-500 underline" data-testid={`admin-sample-clear-${a.id}`}>Usar o valor por defeito</button>}
      <span className="text-xs text-slate-500">mínimo atual {a.sample_min}% · a Direção pode aumentar</span>
    </div>
  );
}

function TwilioAdmin({ a }) {
  const [s, setS] = useState(a.twilio);
  const [nums, setNums] = useState((a.twilio.numbers || []).join("\n"));
  const save = async (enabled) => {
    try {
      const { data } = await api.put(`/platform/agencies/${a.id}/twilio-wa`, { enabled, numbers: nums.split(/[\n,;]+/).map((x) => x.trim()).filter(Boolean) });
      setS(data); setNums(data.numbers.join("\n")); toast.success("WhatsApp pela Twilio guardado");
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  return (
    <div className="space-y-2 border-t border-slate-100 pt-3" data-testid={`admin-twilio-${a.id}`}>
      <label className="flex items-center gap-3 text-sm text-slate-700">
        <Switch checked={s.enabled} onCheckedChange={save} data-testid={`admin-twilio-toggle-${a.id}`} />
        WhatsApp pela Twilio (teste) {!s.configured && <span className="text-xs text-amber-700">faltam as chaves da Twilio</span>}
      </label>
      <textarea rows={2} value={nums} onChange={(e) => setNums(e.target.value)} placeholder="+351 912 345 678" className="w-full max-w-md rounded-xl border border-slate-200 p-2.5 text-sm" data-testid={`admin-twilio-numbers-${a.id}`} />
      <button onClick={() => save(s.enabled)} className="block h-8 px-3 rounded-lg border border-slate-200 text-sm font-semibold hover:bg-slate-50" data-testid={`admin-twilio-numbers-save-${a.id}`}>Guardar números de teste</button>
    </div>
  );
}

function DocExceptions({ a, data, setData }) {
  const [key, setKey] = useState("");
  const names = Object.fromEntries(data.docs.map((d) => [d.key, d]));
  const save = async (k, validity_months, offset_days) => {
    try { setData(await putConfirm(`/platform/agencies/${a.id}/doc-rules`, { key: k, validity_months, offset_days })); toast.success("Exceção guardada"); setKey(""); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const clear = async (k) => {
    try { setData((await api.put(`/platform/agencies/${a.id}/doc-rules`, { key: k, clear: true })).data); toast.success("Exceção removida"); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const rows = Object.entries(a.doc_overrides || {});
  return (
    <div className="border-t border-slate-100 pt-3" data-testid={`admin-doc-exceptions-${a.id}`}>
      <div className="text-sm font-medium text-slate-700">Exceções de validade e antecedência</div>
      <table className="w-full text-sm mt-1"><tbody>
        {rows.map(([k, v]) => [
          <DocRow key={`${k}-${v.validity_months}-${v.offset_days}`} d={{ ...names[k], ...v, key: k }} onSave={save} testid={`admin-exc-${a.id}-${k}`} />,
          <tr key={`${k}-x`}><td colSpan={4} className="pb-2 text-xs"><span className="text-slate-500">Por defeito: {names[k]?.validity_months ?? "—"} meses · {names[k]?.offset_days} dias · </span><button onClick={() => clear(k)} className="text-slate-500 underline" data-testid={`admin-exc-clear-${a.id}-${k}`}>remover exceção</button></td></tr>,
        ])}
        {key && <DocRow d={names[key]} onSave={save} testid={`admin-exc-new-${a.id}`} />}
      </tbody></table>
      <select value={key} onChange={(e) => setKey(e.target.value)} className="mt-2 h-9 rounded-md border border-slate-200 bg-white px-2 text-sm" data-testid={`admin-exc-add-${a.id}`}>
        <option value="">Adicionar exceção…</option>
        {data.docs.filter((d) => !a.doc_overrides?.[d.key]).map((d) => <option key={d.key} value={d.key}>{d.name}</option>)}
      </select>
    </div>
  );
}
